import clsx from 'clsx'
import type { TFunction } from 'i18next'
import type { DefaultValues } from 'react-hook-form'
import { useTranslation } from 'react-i18next'

import LeftArrowIcon from '@/assets/icons/chevron-small-left.svg?react'
import RightArrowIcon from '@/assets/icons/chevron-small-right.svg?react'
import CloseIcon from '@/assets/icons/close.svg?react'
import Button from '@/components/Button/Button'
import ButtonIcon from '@/components/ButtonIcon/ButtonIcon'
import Loading from '@/components/Loading'
import { FormLayout } from '@/components/common/FormGrid/FormGrid'
import FormStepper from '@/components/common/FormStepper/FormStepper'
import { useScreenResolution } from '@/hooks/useScreenResolution'

import styles from './CurriculumForm.module.scss'
import BasicInformation from './steps/BasicInformation'
import Basket from './steps/Basket'
import Summary from './steps/Summary'
import useCurriculumForm, {
  type CurriculumFormStep
} from '../hooks/useCurriculumForm'

const getSteps = (t: TFunction<'curriculumsManagement', undefined>) => [
  {
    id: 'basic-info',
    label: t('label.basic-information'),
    content: <BasicInformation />
  },
  {
    id: 'baskets',
    label: t('label.baskets'),
    content: <Basket />
  },
  {
    id: 'summary',
    label: t('label.summary'),
    content: <Summary />
  }
]

type CurriculumFormProps = {
  curriculumId?: string
  defaultValues?: DefaultValues<CurriculumFormStep>
  onSubmit: (formData: CurriculumFormStep) => void
  onSaveAsDraft: (formData: CurriculumFormStep) => void
  isLoading?: boolean
}

const CurriculumForm = (props: CurriculumFormProps) => {
  const { t } = useTranslation('curriculumsManagement')
  const { isMobile } = useScreenResolution()

  const { form, step, handleNext, handleBack } = useCurriculumForm({
    defaultValues: props.defaultValues,
    curriculumId: props.curriculumId
  })

  const steps = getSteps(t)

  const resetDirtyFields = () => form.reset({}, { keepValues: true })

  const handleSaveAsDraft = () => {
    const data = form.getValues()
    resetDirtyFields()
    props.onSaveAsDraft(data)
  }

  const footerButtonsProps = {
    handleBack,
    handleNext,
    step,
    handleSaveAsDraft
  }

  return (
    <Loading
      className={styles.container}
      innerClassName={styles.container}
      spinning={props.isLoading}
    >
      <FormLayout
        className={clsx(styles.form, styles.container)}
        form={form}
        onSubmit={props.onSubmit}
      >
        <FormStepper steps={steps} currentStep={step} />
        <div className={styles.content}>{steps[step]?.content}</div>
        <footer className={styles.footer}>
          {isMobile ? (
            <FooterButtons {...footerButtonsProps} />
          ) : (
            <DesktopFooterButtons {...footerButtonsProps} />
          )}
        </footer>
      </FormLayout>
    </Loading>
  )
}

type FooterButtonsProps = {
  step: number
  handleBack: () => void
  handleNext: () => void
  handleSaveAsDraft: () => void
}

const FooterButtons = (props: FooterButtonsProps) => {
  const { t } = useTranslation('curriculumsManagement')
  const isLastStep = props.step === 2

  return (
    <>
      {props.step > 0 ? (
        <ButtonIcon
          ariaLabel={t('button.back')}
          size="medium"
          variant="secondary"
          onClick={props.handleBack}
        >
          <LeftArrowIcon />
        </ButtonIcon>
      ) : null}

      <div className={styles.buttons}>
        {props.step > 0 ? (
          <Button variant="tertiary" onClick={props.handleSaveAsDraft}>
            {t('button.save-as-draft')}
          </Button>
        ) : null}

        <ButtonIcon size="medium" variant="secondary" asLink to="/curricula">
          <CloseIcon />
        </ButtonIcon>

        {isLastStep ? (
          <ButtonIcon
            ariaLabel={t('button.submit')}
            size="medium"
            variant="primary"
            type="submit"
            key="submit"
          >
            <RightArrowIcon />
          </ButtonIcon>
        ) : (
          <ButtonIcon
            ariaLabel={t('button.next')}
            size="medium"
            variant="primary"
            onClick={props.handleNext}
          >
            <RightArrowIcon />
          </ButtonIcon>
        )}
      </div>
    </>
  )
}

const DesktopFooterButtons = (props: FooterButtonsProps) => {
  const { t } = useTranslation('curriculumsManagement')
  const isLastStep = props.step === 2

  return (
    <>
      {props.step > 0 ? (
        <Button variant="secondary" onClick={props.handleBack}>
          {t('button.back')}
        </Button>
      ) : null}

      <div className={styles.buttons}>
        {props.step > 0 ? (
          <Button variant="tertiary" onClick={props.handleSaveAsDraft}>
            {t('button.save-as-draft')}
          </Button>
        ) : null}

        <Button variant="secondary" asLink to="/curricula">
          {t('button.cancel')}
        </Button>
        {isLastStep ? (
          <Button type="submit" key="submit">
            {t('button.submit')}
          </Button>
        ) : (
          <Button onClick={props.handleNext}>{t('button.next')}</Button>
        )}
      </div>
    </>
  )
}
export default CurriculumForm
